import { isValidLink, noSpaces } from 'utils/validation';
import { INVALID_DOMAINS_ERROR, DOMAIN_EXISTS_ERROR, EMPTY_APPLINKS_JSON } from './constants';
// import { SendingDomain, TrackingDomain, AppSiteAssociations, AndroidAssetLink } from './types';

/**
  Successfully creating a new tracking domain does not return a created object from the API. 
  This function is used to create a default tracking domain model to place into the redux state when a create call gets successfully excecuted.
*/
export const createNewTrackingDomain = (domainName /*: string */) => ({
  domain: domainName,
  isVerified: false,
  isDefault: false,
  useHttps: false,
});

/**
  Simple validation of domain names to see whether: 
  1. the path has a valid regex: [word(s)].[word(s)]
  2. the path does not yet exist.

  if either is true, it returns an error message, else an empty string
*/
export const validateDomainName = (
  name /*: string */,
  existingDomainNames /*: Array<string> */,
  initialState = '',
) => {
  // return no error if name equals the initally provided state (usually an empty string)
  if (name === initialState) {
    return '';
  }

  // whitespace in domain
  if ([...noSpaces(name)].length !== 0) {
    return INVALID_DOMAINS_ERROR;
  }

  // regex is invalid
  if ([...isValidLink(name)].length !== 0) {
    return INVALID_DOMAINS_ERROR;
  }

  // domain name already exists
  if (existingDomainNames.includes(name)) {
    return DOMAIN_EXISTS_ERROR;
  }

  return '';
};

/**
  Helper function used when the editTrackingDomain, editSendingDomain, enableDeepLinkPath, or the upsertAndroidAssetsSaga sagas are called. 
  
  Takes the exisiting domains (or assetLinks), and inserts the updated domain (or asset link) into them for a new redux state.

  Tracking domains primary key is the 'domain', 
  while SendingDmoains's is the 'id',
  and tracking Links validate on both 'domain' and 'appDomain'

  Android asset links validate on 'organisationId' and 'domain'
*/
export const joinUpdatedDomainIntoDomains = (
  domains /*: Array<SendingDomain | TrackingDomain | AppSiteAssociations | AndroidAssetLink> */,
  updatedDomain /*: SendingDomain | TrackingDomain | AppSiteAssociations | AndroidAssetLink */,
  key /*: string */,
  key2 /* ?: string */,
) =>
  domains.map((domain) => {
    if (domain[key] !== updatedDomain[key]) {
      return domain;
    }
    if (key2 && domain[key2] !== updatedDomain[key2]) {
      return domain;
    }
    return updatedDomain;
  });

/**
  Helper function used when the joinUpdatedLegacyAndDestinationAASAIntoDomains is called.
  Takes the exisiting domains (or assetLinks), and inserts the changed legacy domain

  Tracking domains primary key is the 'domain',
  while SendingDmoains's is the 'id',
  and tracking Links validate on both 'domain' and 'appDomain'
*/
export const joinUpdatedLegacyAndDestinationAASAIntoDomains = (
  appSiteAssociations /*: Array<AppSiteAssociations> */,
  updatedLegacyAppSiteAssociation /*: AppSiteAssociations */,
) =>
  appSiteAssociations.map((aasa) => {
    if (aasa.domain !== updatedLegacyAppSiteAssociation.domain) {
      return aasa;
    }

    if (!aasa.appDomain) {
      return updatedLegacyAppSiteAssociation;
    }

    return { ...aasa, enabledForProject: false };
  });

/**
  Helper function used when the setDefaultTrackingDomain saga is called.
  Takes the exisiting domains, and inserts the updated domain into them for a new redux state. And sets all other tracking domains as not default, in the process.
*/
export const setDefaultTrackingDomain = (
  domains /*: Array<TrackingDomain> */,
  newDefaultDomain /*: TrackingDomain */,
) =>
  domains.map((domain) => {
    if (domain.domain !== newDefaultDomain.domain) {
      return { ...domain, isDefault: false };
    }
    return newDefaultDomain;
  });

/**
  Helper function used in the PathSection component and the useValidJson hook.
  Verifies whether or not the json field is empty
*/
export const isJsonFileEmpty = (jsonString /*: string | undefined */, platformIsApple = true) => {
  if (!jsonString) {
    return true;
  }

  if (platformIsApple) {
    // aasa files start with a specific json preset
    return JSON.stringify(jsonString) === EMPTY_APPLINKS_JSON;
  }

  return false;
};
